import React from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import styled from 'styled-components';

import { ErrorCallout, Box, Text, Theme } from 'baukasten';

import { TooLazyToType } from 'types/TooLazyToType';
import { getSubdomain } from 'utils/browser';
import { errorMessage } from 'utils/form';
import { NonAuthenticatedAppLayout } from 'containers/NonAuthenticatedAppLayout';

import messages from './messages';

const StyledAnchor = styled.a`
  color: ${Theme.getColor('blue-dark')};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

type ErrorStateProps = {
  getAccountError?: TooLazyToType;
  getProfileError?: TooLazyToType;
  javascriptError?: TooLazyToType;
};

const ErrorState = ({
  getAccountError,
  getProfileError,
  javascriptError,
}: ErrorStateProps) => {
  let error: React.ReactNode;

  const { formatMessage } = useIntl();
  const handleReload = (e: React.MouseEvent) => {
    e.preventDefault();
    window.location.reload();
  };

  const errorHelp = formatMessage(messages.errorHelp, {
    // href is only for valid anchor semantics; preventDefault stops navigation
    reloading: (
      <StyledAnchor href="#reload" onClick={handleReload}>
        {formatMessage(messages.errorHelpReloading)}
      </StyledAnchor>
    ),
    email: (
      <StyledAnchor href="mailto:support@workpath.com">
        support@workpath.com
      </StyledAnchor>
    ),
  });

  if (javascriptError || getAccountError || getProfileError) {
    let message: MessageDescriptor;

    if (javascriptError) {
      message = messages.javascriptError;
    } else if (getAccountError) {
      message = messages.accountError;
    } else if (getProfileError) {
      message = messages.profileError;
    }

    error = (
      <>
        <Text fontSize={16} color="navy">
          {formatMessage(message)}
        </Text>

        <Text fontSize={16} color="navy" mt={4}>
          {errorHelp}
        </Text>

        <Box mt={2}>
          <ErrorCallout
            message={errorMessage(
              javascriptError || getAccountError || getProfileError
            )}
          />

          <Box mt={2}>
            <Text fontSize={12} color="grey-dark">
              {formatMessage(messages.debugInformation, {
                subdomain: getSubdomain(),
                userAgent: navigator.userAgent,
              })}
            </Text>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <NonAuthenticatedAppLayout hideSecondaryContent>
      <Text fontSize={32} fontWeight="bold" color="navy">
        {formatMessage(messages.heading)}
      </Text>

      <Box mt={6}>{error}</Box>
    </NonAuthenticatedAppLayout>
  );
};

export default ErrorState;
